import DynamicAction from '@/components/ui/dynamic-action'
import ImageContentful from './ui/image-contentful'
import { isTypeComponentCtaLink, isTypeHeaderCardLink, TypeHeaderCard } from '@/types/ctf'
import { cx } from '@/utils/strings'
import { useTranslations } from '@/utils/locale'

type HeaderCardProps = TypeHeaderCard<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  className?: string
  onLinkClick?: () => void
  menuOpen?: boolean
}

export default function HeaderCard({
  className,
  headline,
  subtext,
  links = [],
  image,
  onLinkClick,
  menuOpen,
}: HeaderCardProps) {
  const { t } = useTranslations()

  return (
    <div className={className}>
      <h5 className={cx('text-2xl text-indigoBlue dark:text-secondary-50 flex', subtext ? 'mb-1' : 'mb-[1.125rem]')}>
        {image && (
          <div className="hidden md:block w-8 h-8 mr-[0.875rem]">
            <ImageContentful
              {...image.fields}
              title={image.fields.title || ''}
              className="[&_svg]:w-full [&_svg]:h-full [&_path]:fill-indigoBlue"
            />
          </div>
        )}
        <span>{headline}</span>
      </h5>
      {subtext && <p className="text-base leading-6 mb-[1.125rem] text-[#545E66]">{subtext}</p>}
      {links.length > 0 && (
        <div className="flex flex-col gap-[1.125rem]">
          {links.map((link, index) => {
            if (!link) return null

            if (isTypeComponentCtaLink(link)) {
              return (
                <div key={link.sys.id}>
                  <DynamicAction
                    className="font-normal! leading-[120%] py-0!"
                    onClick={onLinkClick}
                    {...link}
                    size="Smaller"
                    tabIndex={!menuOpen && -1}
                  />
                </div>
              )
            }

            if (isTypeHeaderCardLink(link)) {
              return (
                <div key={link.sys.id} className="relative">
                  <div className={cx(link.fields.displayNewTag ? 'flex gap-x-3 gap-y-1 items-center' : 'contents')}>
                    {link.fields.displayNewTag && (
                      <span className="bg-primaryBlue text-white text-sm uppercase px-3 py-1 rounded-lg">
                        {t('New')}
                      </span>
                    )}
                    <DynamicAction
                      className="font-normal! leading-[120%] py-0!"
                      onClick={onLinkClick}
                      {...link.fields.link}
                      size="Smaller"
                      tabIndex={!menuOpen && -1}
                    />
                  </div>
                  {link.fields.description && (
                    <p className="text-sm text-secondary-400 dark:text-secondary-200 md:hidden mb-0">
                      {link.fields.description}
                    </p>
                  )}
                  {link.fields.shortDescription && (
                    <p className="text-sm text-secondary-400 dark:text-secondary-200 hidden md:block mb-0">
                      {link.fields.shortDescription}
                    </p>
                  )}
                </div>
              )
            }
          })}
        </div>
      )}
    </div>
  )
}
